import { type JSX, useEffect, useRef } from 'react';
import { Modal } from './modal';
import { cx, ProductPhoto } from './primitives';

/**
 * Looking at a picture properly, in one implementation.
 *
 * A product photograph is authored at whatever resolution the bakery shot it
 * and then shown, everywhere, in a square of at most 132px - which is enough to
 * recognise a croissant and not nearly enough to check whether the one you
 * uploaded is in focus, is the right pastry, or has somebody's thumb in it. The
 * only way to see it was to open the R2 url in a second tab, and nothing on any
 * screen offered one.
 *
 * So: every picture on a staff surface opens. `ProductPhoto` and `BrandMark`
 * take an `onZoom`, which turns the box into a button; the button opens
 * `PhotoViewer`, which shows the full picture, and - when the product has more
 * than one - arrows, a thumbnail carousel and the left/right keys to walk them.
 *
 * The GUEST equivalent is `PhotoCarousel`, which is a different thing on
 * purpose: a guest holding a phone swipes a strip and reads the dots, and does
 * not want a modal over a modal on the item sheet that is already one.
 *
 * Both are presentational and label-free - every string is passed in already
 * translated (NFR-006), because this package has no catalogue and four apps
 * with four of them import it.
 */

/** A picture either component can show. The same shape `ProductImage` carries. */
export interface ViewableImage {
  readonly url: string;
  readonly alt: string;
}

export interface PhotoViewerLabels {
  /** Accessible name for the dismiss control. */
  readonly close: string;
  readonly previous: string;
  readonly next: string;
  /** Already interpolated, e.g. "2 of 5". Announced, and drawn under the picture. */
  readonly position: string;
}

export interface PhotoViewerProps {
  readonly images: readonly ViewableImage[];
  /**
   * Which picture is open, or null for closed. Held by the CALLER rather than
   * here: the surface that opened the viewer is the one that knows which
   * thumbnail was clicked, and a viewer holding its own index reopens on the
   * last picture somebody looked at rather than the one they just tapped.
   */
  readonly openIndex: number | null;
  readonly onIndex: (index: number) => void;
  readonly onClose: () => void;
  readonly labels: PhotoViewerLabels;
  /**
   * `contain` (the default) shows the whole picture, letterboxed - which is the
   * point of opening it. `cover` exists for the one case where the crop IS the
   * subject: checking what a square thumbnail will actually throw away.
   */
  readonly fit?: 'contain' | 'cover';
}

/** Full size, with the whole set behind arrows when there is more than one. */
export function PhotoViewer({
  images,
  openIndex,
  onIndex,
  onClose,
  labels,
  fit = 'contain',
}: PhotoViewerProps): JSX.Element | null {
  const stripRef = useRef<HTMLDivElement | null>(null);
  const open = openIndex !== null && openIndex >= 0 && openIndex < images.length;
  const index = open ? openIndex : 0;
  const current = images[index];
  const many = images.length > 1;

  /*
   * Walking with the arrow keys scrolls the thumbnail strip to follow, or the
   * marker for where you are goes off the end of a strip that does not move.
   * `nearest` rather than `center` so a strip that already shows the thumbnail
   * stays where it is instead of jumping under the cursor.
   */
  useEffect(() => {
    if (!open) return;
    const strip = stripRef.current;
    strip?.children[index]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [open, index]);

  /*
   * Left and right walk the set. The listener is on the document rather than on
   * an element, because `showModal` puts focus wherever it likes inside the
   * panel and a handler bound to one box only fires while that box holds it -
   * and because a `<dialog>` in the top layer already has the keyboard to
   * itself, so there is nothing behind it for this to steal keys from. It is a
   * SHORTCUT: the two arrow buttons carry the same actions and are in the tab
   * order, so nothing here is the only route to anything.
   */
  useEffect(() => {
    if (!open || images.length < 2) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      const delta = event.key === 'ArrowLeft' ? -1 : 1;
      onIndex((index + delta + images.length) % images.length);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, index, images.length, onIndex]);

  if (!open || current === undefined) return null;

  const step = (delta: number): void => {
    // Wraps, because a set of photographs is a ring and not a queue: past the
    // last one the next thing to look at is the first one.
    onIndex((index + delta + images.length) % images.length);
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={current.alt === '' ? labels.position : current.alt}
      titleHidden
      size="lg"
      closeLabel={labels.close}
      className="cib-photoviewer"
    >
      <div className="cib-photoviewer__frame">
        <img
          className={cx('cib-photoviewer__image', `cib-photoviewer__image--${fit}`)}
          src={current.url}
          alt={current.alt}
        />

        {many ? (
          <>
            <button
              type="button"
              className="cib-photoviewer__step cib-photoviewer__step--prev"
              aria-label={labels.previous}
              onClick={() => step(-1)}
            >
              <span aria-hidden="true">{'‹'}</span>
            </button>
            <button
              type="button"
              className="cib-photoviewer__step cib-photoviewer__step--next"
              aria-label={labels.next}
              onClick={() => step(1)}
            >
              <span aria-hidden="true">{'›'}</span>
            </button>
          </>
        ) : null}
      </div>

      {many ? (
        <>
          <p className="cib-photoviewer__position" aria-live="polite">
            {labels.position}
          </p>
          <div className="cib-photoviewer__strip" ref={stripRef}>
            {images.map((image, position) => (
              <button
                key={image.url}
                type="button"
                className={cx(
                  'cib-photoviewer__thumb',
                  position === index && 'cib-photoviewer__thumb--current',
                )}
                aria-current={position === index}
                onClick={() => onIndex(position)}
              >
                <img src={image.url} alt={image.alt} />
              </button>
            ))}
          </div>
        </>
      ) : null}
    </Modal>
  );
}

/**
 * Which slide a strip is showing, from how far it has been scrolled.
 *
 * Rounding to the NEAREST slide rather than flooring is what makes a
 * half-scrolled strip name the square filling most of the box - flooring keeps
 * the previous dot lit until the next square has fully arrived, which reads as
 * the indicator lagging the thumb. Null when there is nothing to measure
 * against: a box of zero width is a strip that has not been laid out yet.
 */
export function slideAtOffset(scrollLeft: number, width: number, count: number): number | null {
  if (width <= 0 || count <= 0) return null;
  const settled = Math.round(scrollLeft / width);
  return Math.min(Math.max(settled, 0), count - 1);
}

export interface PhotoCarouselProps {
  readonly images: readonly ViewableImage[];
  /**
   * Announced on the dot row, e.g. "Photo 2 of 5" - and the accessible name of
   * each dot, with its own number. Already translated.
   */
  readonly dotLabel: (position: number, count: number) => string;
  readonly className?: string;
  /** Which picture is showing, and how to say so. Owned here; see `onIndex`. */
  readonly index: number;
  readonly onIndex: (index: number) => void;
}

/**
 * The guest's picture strip: one square at a time, swiped, with the row of dots
 * every phone home screen has already taught (GST-003).
 *
 * Scroll snapping does the swiping - no drag handling, no dependency, and the
 * native inertia a thumb expects. The dots are buttons as well as an indicator,
 * because the same component is used on a laptop where there is nothing to
 * swipe with, and `scrollIntoView` is what moves the strip when one is pressed.
 *
 * A single photograph renders as a plain square with no dots: a carousel of one
 * is a photograph, and a lone dot under it is an invitation to swipe somewhere
 * that does not exist.
 */
export function PhotoCarousel({
  images,
  dotLabel,
  className,
  index,
  onIndex,
}: PhotoCarouselProps): JSX.Element | null {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const first = images[0];
  if (first === undefined) return null;

  if (images.length === 1) {
    return <ProductPhoto className={className} src={first.url} alt={first.alt} />;
  }

  /*
   * Which square the strip has settled on, from the scroll offset rather than
   * from a swipe gesture - so a flick, a trackpad, a pressed dot and a keyboard
   * scroll all report the same way. The arithmetic is `slideAtOffset` so it can
   * be tested without a browser: scroll events are not dispatched to a hidden
   * tab at all, which makes this the one behaviour here that automation cannot
   * observe.
   */
  const onScroll = (): void => {
    const track = trackRef.current;
    if (track === null) return;
    const settled = slideAtOffset(track.scrollLeft, track.clientWidth, images.length);
    if (settled !== null && settled !== index) onIndex(settled);
  };

  /*
   * `scrollTo` on the track, not `scrollIntoView` on the slide. The latter
   * walks every scrollable ANCESTOR - and this strip lives inside a `<dialog>`
   * that is one - so the dot lit up, the page shifted, and the strip itself
   * stayed on the first square. An offset the caller computes cannot be
   * misattributed to the wrong box.
   *
   * Instantly, not smoothly. A dot press is a jump rather than a swipe, and an
   * instant offset LANDS on a snap point rather than racing the snap animation
   * to it - so the strip and the lit dot can never disagree about which square
   * is showing, which is the one thing this pair of controls must not do.
   */
  const show = (position: number): void => {
    onIndex(position);
    const track = trackRef.current;
    if (track === null) return;
    track.scrollTo({ left: position * track.clientWidth });
  };

  return (
    <div className={cx('cib-carousel', className)}>
      <div className="cib-carousel__track" ref={trackRef} onScroll={onScroll}>
        {images.map((image) => (
          <div className="cib-carousel__slide" key={image.url}>
            <ProductPhoto src={image.url} alt={image.alt} />
          </div>
        ))}
      </div>
      <div className="cib-carousel__dots">
        {images.map((image, position) => (
          <button
            key={image.url}
            type="button"
            className={cx('cib-carousel__dot', position === index && 'cib-carousel__dot--current')}
            aria-label={dotLabel(position + 1, images.length)}
            aria-current={position === index}
            onClick={() => show(position)}
          />
        ))}
      </div>
    </div>
  );
}
